import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import { getChequeSettlementSummary } from 'src/redux/slices/cheque';

import { RHFCheckbox } from 'src/components/hook-form';

type Props = {
  chequeId: string;
  // Name of the form field holding the payment amount, in rupees.
  amountField: string;
  name?: string;
};

// "Debtor paid this directly to the supplier's account" — only usable while
// the supplier is still owed at least the entered amount, because the same
// amount is also booked as a supplier settlement.
export default function DirectToSupplierCheckbox({
  chequeId,
  amountField,
  name = 'paidDirectToSupplier',
}: Props) {
  const dispatch = useAppDispatcher();
  const { watch, setValue } = useFormContext();

  const [owedInCents, setOwedInCents] = useState<number | null>(null);

  useEffect(() => {
    dispatch(getChequeSettlementSummary({ chequeId }))
      .unwrap()
      .then((summary) => setOwedInCents(summary.settlementOutstandingInCents))
      .catch(() => setOwedInCents(0));
  }, [dispatch, chequeId]);

  const amountInCents = Math.round((Number(watch(amountField)) || 0) * 100);
  const checked = !!watch(name);

  let disabledReason = '';
  if (owedInCents === null) {
    disabledReason = 'Checking what is still owed to the supplier...';
  } else if (owedInCents <= 0) {
    disabledReason = 'The supplier is already fully settled';
  } else if (amountInCents <= 0 || amountInCents > owedInCents) {
    disabledReason = `Only available for amounts up to ${fCurrencyRupees(
      owedInCents
    )}, which is still owed to the supplier`;
  }

  const disabled = !!disabledReason;

  // Never keep it ticked once the amount no longer fits.
  useEffect(() => {
    if (disabled && checked) {
      setValue(name, false);
    }
  }, [disabled, checked, name, setValue]);

  return (
    <RHFCheckbox
      name={name}
      disabled={disabled}
      label="Debtor paid this directly to the supplier's account"
      helperText={
        disabledReason ||
        `Counts as a recovery and also settles the supplier by the same amount (still owed: ${fCurrencyRupees(
          owedInCents ?? 0
        )})`
      }
    />
  );
}
